<script lang="ts">
	//Helpers
	import { deleteDevice } from 'src/helpers/add_delete_helpers';
	import { capFirstChar } from 'src/helpers/utilities';

	//Services
	import { format } from 'src/services/i18n';
	//Configs
	import { devices_config } from '@devices/index';

	//Stores
	import { app, os } from 'src/stores/session/native-bridge';

	//Design system components
	import { Alert, Button, toast } from '@Shelly/shelly-design-system';

	//Components
	import { controlBleDevice } from 'src/stores/features/ble/control-device';
	import { control_bluetooth_device_android } from 'src/services/native_container_requests_android';
	import { control_bluetooth_device_ios } from 'src/services/native_container_requests_ios';
	import { resetDrawerClose } from 'src/helpers/drawer_helpers';
	import FactoryResetModal from 'src/components/FactoryResetModal.svelte';
	import { BLU_STATUS } from 'src/types/requests/settings/blu_status';
	import type { AlertPropsTypes, ButtonPropsTypes } from '@Shelly/shelly-design-system';

	export let device;

	let showModal = false;
	let initialized = false;

	const type = devices_config[device.type].friendly_name;

	const factoryReset = () => {
		if ($controlBleDevice.connectionState === 2) {
			$os === 'IOS'
				? control_bluetooth_device_ios(device.id, 'factoryReset')
				: control_bluetooth_device_android(device.id, 'factoryReset');
			$os === 'IOS'
				? control_bluetooth_device_ios(device.id, 'disconnect')
				: control_bluetooth_device_android(device.id, 'disconnect');

			toast.open({
				type: 'success',
				title: $format('factory_reset_scc_toast', {
					values: { device_name: device.name },
				}),
				duration: 10000,
			});

			controlBleDevice.updateProcessRunning(false);
			deleteDevice(device.id);
			resetDrawerClose();
		} else {
			$os === 'IOS'
				? control_bluetooth_device_ios(device.id, 'connect')
				: control_bluetooth_device_android(device.id, 'connect');
		}
	};

	const props = {
		title: `${$format('reset_device_to_factory_stngs', {
			default: 'Reset Device to Factory Settings',
		})}?`,
		name: device?.name,
		text: $format('resetting_device_msg', {
			values: { device_name: device.name },
			default:
				'Resetting the device ‘{device_name}’ to its factory settings will erase all data and settings, including its network configuration. To reconnect, use its Access Point network.',
		}),
		alert: {
			heading: $format('resseting_device_alert_msg', {
				default:
					'Resetting the device to factory settings will also delete the device from the app.',
			}),
			type: 'danger' as AlertPropsTypes['type'],
		},
		button_1: $format('button_reset_device', {
			default: 'Reset device',
		}),
		button_1_variant: 'danger' as ButtonPropsTypes['variant'],
		button_2: $format('button_cancel'),
		callback: factoryReset,
	};

	$: switch ($controlBleDevice.currentStatusIndex) {
		// after receiving one of the statuses below remove the initialized state
		case BLU_STATUS.UNHANDLED_STATE:
		case BLU_STATUS.BUTTON_PRESS_REQUIRED:
		case BLU_STATUS.CONNECT_TO_DEVICE:
		case BLU_STATUS.DISCONNECT_FROM_DEVICE:
			initialized = false;
			break;
		// after receiving one of the statuses below add the initialized state
		case BLU_STATUS.DEVICE_OTA_AVAILABLE:
		case BLU_STATUS.DEVICE_OTA_REQUIRED:
		case BLU_STATUS.DEVICE_PROCESSED:
		case BLU_STATUS.BEACON_ENABLED:
		case BLU_STATUS.BEACON_DISABLED:
		case BLU_STATUS.MOTION_LED_ENABLED:
		case BLU_STATUS.MOTION_LED_DISABLED:
		case BLU_STATUS.BLIND_TIME_SET:
		case BLU_STATUS.SENSITIVITY_SET:
		case BLU_STATUS.DISTANCE_RANGE_SET:
		case BLU_STATUS.INTERVAL_MEASURE_SET:
		case BLU_STATUS.MEASURE_VIBRATION_ENABLED:
		case BLU_STATUS.MEASURE_VIBRATION_DISABLED:
		case BLU_STATUS.MIN_ANGLE_THRESHOLD_SET:
		case BLU_STATUS.UTC_OFFSET_SET:
		case BLU_STATUS.TEMPERATURE_OFFSET_SET:
		case BLU_STATUS.HUMIDITY_OFFSET_SET:
		case BLU_STATUS.DARK_THRESHOLD_SET:
		case BLU_STATUS.BRIGHT_THRESHOLD_SET:
		case BLU_STATUS.INVERT_DISPLAY_ENABLED:
		case BLU_STATUS.INVERT_DISPLAY_DISABLED:
		case BLU_STATUS.FAHRENHEIT_ENABLED:
		case BLU_STATUS.FAHRENHEIT_DISABLED:
		case BLU_STATUS.CLOCK_SYNC_ENABLED:
		case BLU_STATUS.CLOCK_SYNC_DISABLED:
		case BLU_STATUS.POWER_SAVER_ENABLED:
		case BLU_STATUS.POWER_SAVER_DISABLED:
		case BLU_STATUS.ZIGBEE_ENABLED:
		case BLU_STATUS.ZIGBEE_DISABLED:
		case BLU_STATUS.SMOKE_THRESHOLD_SET:
		case BLU_STATUS.TEMPERATURE_THRESHOLD_SET:
		case BLU_STATUS.FIRE_GROUP_SET:
		case BLU_STATUS.CLOCK_MODE_SET:
			initialized = true;
			break;
		default:
			break;
	}
</script>

<Alert
	class="mb-s"
	heading={$format('alert_gble_factory_reset_description', {
		default: `Resetting your ${type} will revert it to its factory settings and delete it from the account.`,
		values: { type },
	})}
/>

<Button disabled={!initialized || !$app} size="m" width="full" on:click={() => (showModal = true)}
	>{capFirstChar($format('button_factory_reset'))}</Button
>

{#if showModal}
	<FactoryResetModal bind:showModal {props} />
{/if}
